import { useSelector } from 'react-redux';
import { IFarmResource } from '../../../entities/farms.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { selectAccount } from '../../../store/auth/auth.selector';
import { CheckboxButton, Dropdown, Input } from '../../shared';
import { translate } from '../../../lib/lang.helper';
import { numToStr, getUnitOfSpecie } from '../../../lib/common.helpers';
import { StageLabelSpan } from '../../CommonViews';

interface IMusselReseed {
  lines: Array<{
    line_id: number;
    line_length?: number;
  }>;
  stage?: number;
  density?: number;
  spacing?: number;
  submersion?: number;
  drop?: number;
}

interface IMusselProps {
  farmsData: IFarmResource[];
  farms: IFarmResource[];
  setFarms: (fs: IFarmResource[]) => void;
  form: IMusselReseed | undefined;
  setForm: (f: IMusselReseed | undefined) => void;
  farmId: number;
  currentStage?: number;
  disabled?: boolean;
}

export const MusselHarvestReseedForm = ({
  farmsData,
  farms,
  setFarms,
  form,
  setForm,
  farmId,
  currentStage,
  disabled,
}: IMusselProps) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const selectFarms = (v: string[]) => {
    setFarms(farmsData.filter(x => v.includes(x.id.toString())));
    setForm({ ...form, lines: [] });
  };
  const selectLines = (v: string[]) => {
    setForm({ ...form, lines: v.map(x => ({ line_id: Number(x) })) });
  };
  const updateLine = (line_id: number, val: string) => {
    if (!form) return;

    let tmp = [...form.lines];
    const i = tmp.findIndex(x => x.line_id === line_id);
    if (i >= 0) {
      tmp[i].line_length = val.length > 0 ? Number(val) : undefined;
    }
    setForm({ ...form, lines: tmp });
  };
  const updateForm = (key: keyof IMusselReseed, value: any) => {
    if (!form) return;

    value = value.length <= 0 ? undefined : Number(value);
    setForm({ ...form, [key]: value });
  };

  const optionLines =
    farms?.flatMap(x => x.lines).filter(x => x.crop_type === 'MUSSEL') ?? [];

  return (
    <div className='pl-17'>
      <div className='mb-17'>
        <CheckboxButton
          label={'Put spat new line'}
          onChange={e =>
            setForm(
              e.target.checked
                ? {
                    lines: [],
                    stage: currentStage != null ? currentStage + 1 : 1,
                  }
                : undefined,
            )
          }
          checked={!!form}
          disabled={disabled}
        />
      </div>
      {!!form && (
        <div className='mb-24'>
          <div className='mb-12'>
            <Dropdown
              label={translate(lang, 'Select farm')}
              options={farmsData.map(x => ({
                id: x.id.toString(),
                value: x.id.toString(),
                label: `${x.name} - ${x.farm_number}`,
              }))}
              value={farms?.map(x => x.id.toString()) as any}
              onChange={(v: any) => selectFarms(v)}
              mode='multiple'
            />
          </div>
          <div className='mb-12'>
            <Dropdown
              mode='multiple'
              label={translate(lang, 'Select lines')}
              options={
                optionLines.map(x => ({
                  id: x.id.toString(),
                  value: x.id.toString(),
                  label: `${x.line_name} (${
                    farms?.find(f => f.lines.some(l => l.id === x.id))?.name
                  })`,
                })) ?? []
              }
              value={form.lines.map(x => x.line_id.toString()) as any}
              onChange={(v: any) => selectLines(v)}
            />
          </div>
          <div className='d-flex mb-12' style={{ columnGap: 15 }}>
            {form.lines.map(x => (
              <div key={x.line_id} style={{ flexBasis: '30%' }}>
                <Input
                  label={`Length of Line ${
                    optionLines.find(y => y.id === x.line_id)?.line_name
                  }`}
                  value={numToStr(x.line_length)}
                  onChange={e => updateLine(x.line_id, e.target.value)}
                  type='number'
                  unit={getUnitOfSpecie(unitType, 'distance_m')}
                />
              </div>
            ))}
          </div>
          <div className='mb-12'>
            <Dropdown
              label={translate(lang, 'Stage')}
              options={[1, 2, 3, 4].map(x => ({
                id: x.toString(),
                value: x.toString(),
                label: <StageLabelSpan stage={x} farmId={farmId} />,
              }))}
              value={form.stage?.toString()}
              onChange={(v: any) => setForm({ ...form, stage: Number(v) })}
            />
          </div>
          <div className='d-flex mb-7'>
            <div className='w-100 mr-7'>
              <Input
                label={translate(lang, 'Density')}
                value={numToStr(form.density)}
                onChange={e => updateForm('density', e.target.value)}
                type='number'
                unit={getUnitOfSpecie(unitType, 'spat_mer_m')}
              />
            </div>
            <div className='w-100 ml-7'>
              <Input
                label={translate(lang, 'Spacing')}
                value={numToStr(form.spacing)}
                onChange={e => updateForm('spacing', e.target.value)}
                type='number'
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
          </div>
          <div className='d-flex mb-17'>
            <div className='w-100 mr-7'>
              <Input
                label={translate(lang, 'Submersion')}
                value={numToStr(form.submersion)}
                onChange={e => updateForm('submersion', e.target.value)}
                type='number'
                unit={getUnitOfSpecie(unitType, 'distance_m')}
              />
            </div>
            <div className='w-100 ml-7'>
              <Input
                label={translate(lang, 'Drop')}
                value={numToStr(form.drop)}
                onChange={e => updateForm('drop', e.target.value)}
                type='number'
                unit={getUnitOfSpecie(unitType, 'distance_m')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface IOysterReseed {
  lines: Array<{
    line_id: number;
    basket_count?: number;
    density?: number;
  }>;
  stage?: number;
  spacing?: number;
}

interface IOysterProps {
  farmsData: IFarmResource[];
  farms: IFarmResource[];
  setFarms: (f: IFarmResource[]) => void;
  form: IOysterReseed | undefined;
  setForm: (f: IOysterReseed | undefined) => void;
  farmId: number;
  currentStage?: number;
  disabled?: boolean;
}

export const OysterHarvestReseedForm = ({
  farmsData,
  farms,
  setFarms,
  form,
  setForm,
  farmId,
  currentStage,
  disabled,
}: IOysterProps) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const selectFarms = (v: string[]) => {
    setFarms(farmsData.filter(x => v.includes(x.id.toString())));
    setForm({ ...form, lines: [] });
  };
  const selectLines = (v: string[]) => {
    setForm({ ...form, lines: v.map(x => ({ line_id: Number(x) })) });
  };
  const updateLine = (
    line_id: number,
    key: 'basket_count' | 'density',
    val: string,
  ) => {
    if (!form) return;

    let tmp = [...form.lines];
    const i = tmp.findIndex(x => x.line_id === line_id);
    if (i >= 0) {
      tmp[i][key] = val.length > 0 ? Number(val) : undefined;
    }
    setForm({ ...form, lines: tmp });
  };
  const updateForm = (key: keyof IOysterReseed, value: any) => {
    if (!form) return;

    value = value.length <= 0 ? undefined : Number(value);
    setForm({ ...form, [key]: value });
  };

  const optionLines =
    farms?.flatMap(x => x.lines).filter(x => x.crop_type === 'OYSTER') ?? [];

  return (
    <div className='pl-17'>
      <div className='mb-17'>
        <CheckboxButton
          label={'Put spat new line'}
          onChange={e =>
            setForm(
              e.target.checked
                ? {
                    lines: [],
                    stage: currentStage != null ? currentStage + 1 : 1,
                  }
                : undefined,
            )
          }
          checked={!!form}
          disabled={disabled}
        />
      </div>
      {!!form && (
        <div className='mb-24'>
          <div className='mb-12'>
            <Dropdown
              label={translate(lang, 'Select farm')}
              options={farmsData.map(x => ({
                id: x.id.toString(),
                value: x.id.toString(),
                label: `${x.name} - ${x.farm_number}`,
              }))}
              value={farms?.map(x => x.id.toString()) as any}
              onChange={(v: any) => selectFarms(v)}
              mode='multiple'
            />
          </div>
          <div className='mb-12'>
            <Dropdown
              mode='multiple'
              label={translate(lang, 'Select lines')}
              options={
                optionLines.map(x => ({
                  id: x.id.toString(),
                  value: x.id.toString(),
                  label: `${x.line_name} (${
                    farms?.find(f => f.lines.some(l => l.id === x.id))?.name
                  })`,
                })) ?? []
              }
              value={form.lines.map(x => x.line_id.toString()) as any}
              onChange={(v: any) => selectLines(v)}
            />
          </div>
          {form.lines.map(x => {
            const ln = optionLines.find(y => y.id === x.line_id);
            return ln ? (
              <div key={ln?.id} className='d-flex mb-7'>
                <div className='w-100 mr-12'>
                  <Input
                    label={`Line ${ln.line_name}: Number of ${ln.farming_method}`}
                    value={numToStr(x.basket_count)}
                    onChange={e =>
                      updateLine(x.line_id, 'basket_count', e.target.value)
                    }
                    type='number'
                  />
                </div>
                <div className='w-100 ml-12'>
                  <Input
                    label={`Line ${ln.line_name}: ${ln.farming_method} density`}
                    value={numToStr(x.density)}
                    onChange={e =>
                      updateLine(x.line_id, 'density', e.target.value)
                    }
                    type='number'
                  />
                </div>
              </div>
            ) : (
              <></>
            );
          })}
          <div className='mb-12'>
            <Dropdown
              label={translate(lang, 'Stage')}
              options={[1, 2, 3, 4].map(x => ({
                id: x.toString(),
                value: x.toString(),
                label: <StageLabelSpan stage={x} farmId={farmId} />,
              }))}
              value={form.stage?.toString()}
              onChange={(v: any) => setForm({ ...form, stage: Number(v) })}
            />
          </div>
          <div className='mb-17'>
            <Input
              label={translate(lang, 'Spacing')}
              value={numToStr(form.spacing)}
              onChange={e => updateForm('spacing', e.target.value)}
              type='number'
              unit={getUnitOfSpecie(unitType, 'size_mm')}
            />
          </div>
        </div>
      )}
    </div>
  );
};
